import { useUser } from "components/clerk";
import { Box, Button, HStack, Icon, Text, Tooltip as RNTooltip, TooltipContent } from "components/ui";
import { ChevronDown } from "lucide-react-native";
import React, { useMemo } from "react";
import { Item, ItemAvailability, useGetCartCurrentQuery } from "store/abcoApi";
import { ParagraphSmall, Typography } from "../ui/Typography";
import { Platform } from "react-native";
import Tooltip from "rc-tooltip";
import "rc-tooltip/assets/bootstrap_white.css";

type Props = {
	item: Item;
	size?: number;
	showOnlyOtherLocations?: boolean;
	showOnlyStockStatus?: boolean;
	location?: number | null;
};

// Reusable tooltip locations component
const LocationsTooltipContent = ({ item, size }: { item: Item; size: number }) => (
	<TooltipContent gap={5} paddingVertical={6} bg="$basicwhite" borderColor="$primarylight" borderWidth={1}>
		{item?.itemAvailability?.map((ia: ItemAvailability) => (
			<HStack key={ia.locationId} alignItems="center">
				<Box w={45} alignItems="center">
					<Text style={{ fontWeight: "bold" }}>{ia.locationName}</Text>
				</Box>
				<Text>
					<StatusText itemAvailability={ia} size={size} />
				</Text>
			</HStack>
		))}
	</TooltipContent>
);

// Web-specific tooltip content component
const WebLocationsTooltipContent = ({ item, size }: { item: Item; size: number }) => (
	<Box>
		{item?.itemAvailability?.map((ia: ItemAvailability) => (
			<HStack backgroundColor="white" key={ia.locationId} alignItems="center">
				<Box w={45} alignItems="center">
					<Text style={{ fontWeight: "bold" }}>{ia.locationName}</Text>
				</Box>

				<div>
					<StatusText itemAvailability={ia} size={size} />
				</div>
			</HStack>
		))}
	</Box>
);

// Reusable status indicator component
const StatusIndicator = ({ color, text, size }: { color: string; text: string; size: number }) => (
	<HStack alignItems="center" gap={8}>
		<Box borderRadius="$full" alignItems="center" justifyContent="center" width={size} height={size} backgroundColor={color}></Box>
		<ParagraphSmall>{text}</ParagraphSmall>
	</HStack>
);

const ItemStatusBadge = ({ item, size = 20, showOnlyOtherLocations = false, showOnlyStockStatus = false, location = null }: Props) => {
	const { data: cart, isLoading, isFetching, isError, error, currentData } = useGetCartCurrentQuery();
	const { isSignedIn } = useUser();

	var locationId = null as number | null;
	if (cart && cart.locationId) {
		locationId = cart.locationId;
	}

	if (location !== null) {
		locationId = location;
	}

	// Find the availability for the current location
	const itemAvailability = useMemo(() => {
		return item.itemAvailability?.find((ia) => ia.locationId === locationId);
	}, [item.itemAvailability, locationId]);

	const availabilityStatus = useMemo(() => {
		// Default values
		const result = {
			allInStock: false,
			allSpecialOrders: false,
			allLowStock: false,
			allOutOfStock: false,
			allDiscontinued: false,
			lowestQuantity: undefined as number | undefined,
			hasDiscontinued: false,
		};

		// If there's no itemAvailability data or item is undefined, return default values
		if (!item || !item?.itemAvailability || item.itemAvailability.length === 0) {
			return result;
		}

		// Calculate all availability flags
		result.allInStock = item.itemAvailability.every((ia: ItemAvailability) => ia.status === "InStock");
		result.allSpecialOrders = item.itemAvailability.every((ia: ItemAvailability) => ia.status === "OutOfStock" && ia.classification === "Special Order Only");
		result.allOutOfStock = item.itemAvailability.every((ia: ItemAvailability) => ia.status === "OutOfStock");
		result.allLowStock = item.itemAvailability.every((ia: ItemAvailability) => ia.status === "LowStock");
		result.allDiscontinued = item.itemAvailability.every((ia: ItemAvailability) => ia.classification === "Discontinued");

		// Find the lowest quantity among all locations for low stock items
		if (result.allLowStock) {
			result.lowestQuantity = Math.min(...item.itemAvailability.filter((ia: ItemAvailability) => ia.status === "LowStock").map((ia: ItemAvailability) => ia.quantity || 0));

			// Check if any low stock items are discontinued
			result.hasDiscontinued = item.itemAvailability.some((ia: ItemAvailability) => ia.status === "LowStock" && ia.classification === "Discontinued");
		}

		return result;
	}, [item?.itemAvailability]);

	// If showOnlyOtherLocations is true, always show "See other locations" regardless of availability
	if (showOnlyOtherLocations) {
		const hasMatchingLocation = locationId && item.itemAvailability && item.itemAvailability.find((loc) => loc.locationId === locationId);
		if (Platform.OS === "web") {
			return (
				<Box marginLeft={"auto"} position="relative" zIndex={9999} cursor="pointer">
					<Tooltip
						overlayInnerStyle={{ background: "white", zIndex: 10000 }}
						showArrow={false}
						styles={{ root: { borderRadius: 6, background: "white", zIndex: 10000 } }}
						transitionName="rc-tooltip-zoom"
						placement="bottom"
						trigger={["hover"]}
						overlay={<WebLocationsTooltipContent item={item} size={size} />}
					>
						<HStack cursor="pointer" alignItems="center" gap={8}>
							<ParagraphSmall>Check stock</ParagraphSmall>
							<Icon as={ChevronDown} color="$basicblack" />
						</HStack>
					</Tooltip>
				</Box>
			);
		}
		return (
			<RNTooltip
				placement="bottom"
				trigger={(triggerProps) => {
					return (
						<Box {...triggerProps} marginLeft={"auto"}>
							<Button>
								<HStack alignItems="center" gap={8}>
									<ParagraphSmall>{hasMatchingLocation ? "See other locations" : "Check stock"}</ParagraphSmall>
									<Icon as={ChevronDown} color="$basicblack" />
								</HStack>
							</Button>
						</Box>
					);
				}}
			>
				<LocationsTooltipContent item={item} size={size} />
			</RNTooltip>
		);
	}

	// If showOnlyStockStatus is true, only show the stock status for the current location
	if (showOnlyStockStatus) {
		// If we have a cart with locationId
		if (cart && cart.locationId && item.itemAvailability) {
			const locationAvailability = item.itemAvailability.find((ia) => ia.locationId === locationId);
			return (
				<Box marginLeft={"auto"}>
					<StatusText itemAvailability={locationAvailability} size={size} />
				</Box>
			);
		}
		// If no cart or no matching location, don't render anything
		return null;
	}

	if (!locationId) {
		// If there's no itemAvailability data, default to Out of Stock
		if (!item?.itemAvailability || item.itemAvailability.length === 0) {
			return (
				<Box marginLeft={"auto"}>
					<StatusIndicator color="$errorbright" text="Out of Stock" size={size} />
				</Box>
			);
		}

		// If we have a clear status (allInStock, allSpecialOrders, allLowStock), don't use tooltip
		if (availabilityStatus?.allInStock) {
			return (
				<Box marginLeft={"auto"}>
					<StatusIndicator color="$successbright" text="In Stock" size={size} />
				</Box>
			);
		}

		if (availabilityStatus?.allSpecialOrders) {
			return (
				<Box marginLeft={"auto"}>
					<StatusIndicator color="$warningbright" text="Special Order Only" size={size} />
				</Box>
			);
		}

		if (availabilityStatus?.allDiscontinued) {
			return (
				<Box marginLeft={"auto"}>
					<StatusIndicator color="$errorbright" text="Discontinued" size={size} />
				</Box>
			);
		}

		if (availabilityStatus?.allOutOfStock) {
			return (
				<Box marginLeft={"auto"}>
					<StatusIndicator color="$errorbright" text="Out of Stock" size={size} />
				</Box>
			);
		}

		if (availabilityStatus?.allLowStock) {
			const lowStockText = availabilityStatus.hasDiscontinued ? `Discontinued ${availabilityStatus.lowestQuantity} left` : `Only ${availabilityStatus.lowestQuantity} left`;

			return (
				<Box marginLeft={"auto"}>
					<StatusIndicator color="$warningbright" text={lowStockText} size={size} />
				</Box>
			);
		}

		if (Platform.OS === "web") {
			return (
				<Box marginLeft={"auto"} position="relative" zIndex={9999}>
					<Tooltip
						overlayInnerStyle={{ background: "white", zIndex: 10000 }}
						showArrow={false}
						styles={{ root: { borderRadius: 6, background: "white", zIndex: 10000 } }}
						transitionName="rc-tooltip-zoom"
						placement="bottom"
						trigger={["hover"]}
						overlay={<WebLocationsTooltipContent item={item} size={size} />}
					>
						<HStack cursor="pointer" alignItems="center" gap={8}>
							<ParagraphSmall>Check stock</ParagraphSmall>
							<Icon as={ChevronDown} color="$basicblack" />
						</HStack>
					</Tooltip>
				</Box>
			);
		}
		// Only show the tooltip for mixed statuses
		return (
			<RNTooltip
				placement="bottom"
				trigger={(triggerProps) => {
					return (
						<Box {...triggerProps} marginLeft={"auto"}>
							<Button>
								<>
									<ParagraphSmall>Check stock</ParagraphSmall>
									<Icon as={ChevronDown} color="$basicblack" />
								</>
							</Button>
						</Box>
					);
				}}
			>
				<LocationsTooltipContent item={item} size={size} />
			</RNTooltip>
		);
	}

	return (
		<Box marginLeft={"auto"}>
			<StatusText itemAvailability={itemAvailability} size={size} />
		</Box>
	);
};

const StatusText = ({ itemAvailability, size }: { itemAvailability: ItemAvailability | undefined; size: number }) => {
	// Calculate status properties based on availability
	const statusProps = useMemo(() => {
		if (!itemAvailability?.status) {
			return null;
		}

		const props = {
			renderedStatusColor: "$successbright",
			renderedTextColor: "$basicwhite",
			renderedText: "",
		};

		const { status, classification, quantity } = itemAvailability;

		switch (status) {
			case "OutOfStock":
				props.renderedStatusColor = "$errorbright";
				props.renderedText = "Out of Stock";

				if (classification === "Discontinued") {
					props.renderedText = "Discontinued";
				} else if (classification === "Special Order Only") {
					props.renderedStatusColor = "$warningbright";
					props.renderedText = "Special Order Only";
				}

				if (classification === "Discontinued") {
					props.renderedStatusColor = "$errorbright";
					props.renderedText = "Discontinued";
				}

				break;

			case "InStock":
				props.renderedStatusColor = "$successbright";
				props.renderedText = "In Stock";
				break;

			case "LowStock":
				props.renderedStatusColor = "$warningbright";
				props.renderedText = `Only ${quantity} left`;

				if (classification === "Discontinued") {
					props.renderedText = `Discontinued ${quantity} left`;
				}
				break;

			default:
			// Keep defaults
		}

		return props;
	}, [itemAvailability]);

	if (!statusProps) {
		return null;
	}

	return (
		<Box gap={8} flexDirection="row" alignItems="center">
			<Box borderRadius="$full" alignItems="center" justifyContent="center" width={size} height={size} backgroundColor={statusProps.renderedStatusColor as any} />
			<Box>
				<Typography.ParagraphSmall>{statusProps.renderedText}</Typography.ParagraphSmall>
			</Box>
		</Box>
	);
};

export default ItemStatusBadge;
